<template>
  <div class="notification-popover" v-if="visible" ref="popoverRef">
    <div class="popover-header">
      <span :class="['tab', {active: tab==='read'}]" @click="changeTab('read')">
        Read
        <span v-if="tab==='read'" class="tab-underline"></span>
      </span>
      <span :class="['tab', {active: tab==='unread'}]" @click="changeTab('unread')">
        Unread
        <span v-if="tab==='unread'" class="tab-underline"></span>
      </span>
    </div>
    <div class="messages-list">
      <div v-if="loading" class="loading">Loading...</div>
      <div v-else>
        <div v-for="msg in messages" :key="msg.id" class="message-item" @click="onMessageClick(msg)">
          <div class="title" :title="msg.title">{{ msg.title }}</div>
          <div class="time">{{ formatTime(msg.createdAt) }}</div>
        </div>
        <div v-if="messages.length === 0" class="empty">No messages</div>
      </div>
    </div>
    <div v-if="messages.length > 0" class="read-more" @click="goToMessages">Read More</div>
  </div>
</template>
<script setup>
import { ref, watch, onMounted, onUnmounted } from 'vue'
import { getMessagesList } from '../api/modules/notification'
import { useRouter } from 'vue-router'

const props = defineProps({ visible: Boolean })
const emit = defineEmits(['close'])

const tab = ref('unread')
const messages = ref([])
const loading = ref(false)
const router = useRouter()
const popoverRef = ref(null)

const fetchMessages = async () => {
  loading.value = true
  try {
    const res = await getMessagesList(tab.value)
    messages.value = Array.isArray(res.body) ? res.body : []
  } catch (e) {
    messages.value = []
  }
  loading.value = false
}

// 点击外部关闭弹框
const handleClickOutside = (event) => {
  if (props.visible && popoverRef.value && !popoverRef.value.contains(event.target)) {
    emit('close')
  }
}

// 监听 visible 变化来添加/移除事件监听器
watch(() => props.visible, (visible) => {
  if (visible) {
    // 延迟添加事件监听器，避免立即触发
    setTimeout(() => {
      document.addEventListener('click', handleClickOutside)
    }, 0)
    fetchMessages()
  } else {
    document.removeEventListener('click', handleClickOutside)
  }
})

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside)
})

// 只在弹出时切换tab才调接口
watch(tab, () => {
  if (props.visible) fetchMessages()
})

function changeTab(t) { tab.value = t }

// 点击消息项跳转到消息页面
function onMessageClick(msg) {
  emit('close')
  const current = router.currentRoute.value
  if (current.path === '/messages' && String(current.query.groupId) === String(msg.groupId)) {
    window.dispatchEvent(new Event('force-refresh-messages'))
  } else {
    router.push({
      path: '/messages',
      query: { groupId: msg.groupId }
    })
  }
}

function goToMessages() {
  emit('close')
  router.push('/messages')
}

function formatTime(time) {
  if (!time) return ''
  const d = new Date(time)
  if (isNaN(d.getTime())) return time
  const now = new Date()
  const isToday = d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth() && d.getDate() === now.getDate()
  if (isToday) {
    // 补零
    const pad = n => n < 10 ? '0' + n : n
    return pad(d.getHours()) + ':' + pad(d.getMinutes())
  } else {
    // MM/DD/YYYY
    return (d.getMonth() + 1).toString().padStart(2, '0') + '/' + d.getDate().toString().padStart(2, '0') + '/' + d.getFullYear()
  }
}
</script>
<style scoped>
.notification-popover {
  position: absolute;
  top: 36px;
  right: 0;
  width: 340px;
  min-height: 220px;
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 4px 16px rgba(0,0,0,0.13);
  z-index: 1000;
  padding: 0 0 1rem 0;
  font-size: 1rem;
  display: flex;
  flex-direction: column;
}
.popover-header {
  display: flex;
  position: relative;
  border-bottom: 1px solid #eee;
  height: 4rem;
  min-height: 4rem;
  max-height: 4rem;
  align-items: flex-end;
  background: #fff;
  justify-content: flex-start;
  gap: 2rem;
  padding-left: 1.5rem;
  flex-shrink: 0;
}
.tab {
  text-align: left;
  padding: 0.75rem 0 1rem 0;
  cursor: pointer;
  font-weight: 600;
  color: #888;
  position: relative;
  z-index: 2;
  background: #fff;
}
.tab.active {
  color: #7DA500;
}
.tab-underline {
  display: block;
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  bottom: 0;
  width: 20px;
  height: 4px;
  background: #7DA500;
  z-index: 3;
  margin: 0;
}
.messages-list {
  flex: 1;
  overflow-y: auto;
  padding: 0 1.5rem;
  background: #fff;
  display: flex;
  flex-direction: column;
  position: relative;
  min-height: 15rem;
}
.message-item {
  border-bottom: 1px solid #F2F2F2;
  cursor: pointer;
  background: #fff;
  padding: 0 0 0.8rem 0;
  margin-top: 0.8rem;
}
.message-item:last-child {
  border-bottom: none;
}
.message-item .title {
  font-size: 15px;
  color: #222;
  font-weight: 500;
  margin-bottom: 4px;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  word-break: break-all;
}
.message-item .time {
  font-size: 13px;
  color: #B0B0B0;
}
.empty {
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  text-align: center;
  color: #aaa;
  padding: 0;
  width: 100%;
}
.read-more {
  color: #7DA500;
  font-size: 14px;
  text-align: left;
  padding: 10px 18px 0 1.5rem;
  cursor: pointer;
  font-weight: 500;
  text-decoration: underline;
}
.loading {
  text-align: center;
  color: #888;
  padding: 32px 0;
}
</style> 